import { useState, useEffect } from "react";
import { API } from "../../../api";
import "../../../styles/admin/RoleManagementEditor.css";

export default function RoleManagementEditor() {
	const [activeTab, setActiveTab] = useState("role");
	const [loading, setLoading] = useState(true);
	const [roles, setRoles] = useState([]);
	const [filteredRoles, setFilteredRoles] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [saving, setSaving] = useState(null);
	const [status, setStatus] = useState(null);
	const [editingRole, setEditingRole] = useState(null);
	const [showForm, setShowForm] = useState(false);
	const [formData, setFormData] = useState({
		slug: "",
		label: "",
		description: "",
		priority: null,
	});
	const [originalRoles, setOriginalRoles] = useState([]);

	useEffect(() => {
		fetchRoles();
	}, []);

	useEffect(() => {
		const filtered = roles.filter(role =>
			role.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			role.description?.toLowerCase().includes(searchTerm.toLowerCase())
		);
		setFilteredRoles(filtered);
	}, [searchTerm, roles]);

	const fetchRoles = async () => {
		try {
			setLoading(true);
			const data = await API.get("roles");
			const sorted = Array.isArray(data)
				? [...data].sort((a, b) => {
					const pa = Number(a.priority ?? 0);
					const pb = Number(b.priority ?? 0);
					if (pa !== pb) return pa - pb;
					return (a.label || "").localeCompare(b.label || "");
				})
				: [];
			setRoles(sorted);
			setOriginalRoles(JSON.parse(JSON.stringify(sorted)));
			setStatus(null);
		} catch (error) {
			console.error("Erreur:", error);
			setStatus({ type: "error", message: "Impossible de charger les rôles" });
		} finally {
			setLoading(false);
		}
	};

	const handleEditRole = (role) => {
		setEditingRole(role);
		setFormData({
			slug: role.slug || "",
			label: role.label || "",
			description: role.description || "",
			priority: role.priority || null,
		});
		setShowForm(true);
		setStatus(null);
	};

	const handleNewRole = () => {
		setEditingRole(null);
		setFormData({
			slug: "",
			label: "",
			description: "",
			priority: null,
		});
		setShowForm(true);
		setStatus(null);
	};

	const handleCancel = () => {
		setShowForm(false);
		setEditingRole(null);
		setFormData({
			slug: "",
			label: "",
			description: "",
			priority: null,
		});
	};

	const handleFormChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: type === "checkbox" ? checked : value
		}));
	};

	const handleSaveRole = async (e) => {
		e.preventDefault();

		if (!formData.slug.trim()) {
			setStatus({ type: "error", message: "L'identifiant (slug) du rôle est obligatoire" });
			return;
		}

		if (!formData.label.trim()) {
			setStatus({ type: "error", message: "Le nom (label) du rôle est obligatoire" });
			return;
		}

		try {
			setSaving(true);
			setStatus(null);

			if (editingRole) {
				// Modification
				await API.put("roles", { slug: editingRole.slug }, {
					label: formData.label,
					description: formData.description,
					priority: formData.priority,
				});
				setStatus({ type: "success", message: `Rôle "${formData.label}" mis à jour avec succès` });
			} else {
				// Création
				await API.post("roles", {
					slug: formData.slug,
					label: formData.label,
					description: formData.description,
					priority: formData.priority,
				});
				setStatus({ type: "success", message: `Rôle "${formData.label}" créé avec succès` });
			}

			await fetchRoles();
			handleCancel();
			setTimeout(() => setStatus(null), 3000);
		} catch (error) {
			console.error("Erreur:", error);
			setStatus({ type: "error", message: `Erreur lors de la ${editingRole ? "modification" : "création"}` });
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteRole = async (role) => {
		if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le rôle "${role.label}" ?`)) {
			return;
		}

		try {
			setSaving(role.slug);
			setStatus(null);

			await API.delete("roles", {
				slug: role.slug,
			});

			setStatus({ type: "success", message: `Rôle "${role.label}" supprimé avec succès` });
			await fetchRoles();
			setTimeout(() => setStatus(null), 3000);
		} catch (error) {
			console.error("Erreur:", error);
			setStatus({ type: "error", message: "Erreur lors de la suppression" });
		} finally {
			setSaving(null);
		}
	};

	const tabs = [
		{ id: "role", label: "Rôles" },
		{ id: "permission", label: "Permissions" },
	];

	if (loading) {
		return (
			<div className="role-editor-container">
				<div className="role-loader">
					<div className="role-spinner"></div>
					<p>Chargement de la gestion des rôles...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="role-editor-container">
			<div className="role-header-section">
				<h1 className="role-title">Gestion des Rôles & Permissions</h1>
				<p className="role-subtitle">Gérez les rôles utilisateur et leurs permissions d'accès</p>
			</div>

			{status && (
				<div className={`role-status role-status-${status.type}`}>
					<div className="role-status-content">
						<span className="role-status-icon">
							{status.type === "success" ? "✓" : "✕"}
						</span>
						<span>{status.message}</span>
					</div>
				</div>
			)}

			<div className="role-tabs">
				{tabs.map(tab => (
					<button
						key={tab.id}
						className={`role-tab ${activeTab === tab.id ? "role-tab-active" : ""}`}
						onClick={() => setActiveTab(tab.id)}
					>
						{tab.label}
					</button>
				))}
			</div>

			<div className="role-content">
				{activeTab === "role" ? (
					<div className="role-section">
						{!showForm ? (
							<>
								<div className="role-toolbar">
									<div className="role-search-container">
										<input
											type="text"
											className="role-search"
											placeholder="Rechercher un rôle..."
											value={searchTerm}
											onChange={(e) => setSearchTerm(e.target.value)}
										/>
										<svg className="role-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
											<circle cx="11" cy="11" r="8"></circle>
											<path d="m21 21-4.35-4.35"></path>
										</svg>
									</div>
									<button className="role-btn role-btn-primary" onClick={handleNewRole}>
										+ Ajouter un rôle
									</button>
								</div>

								<div className="role-list">
									{filteredRoles.length > 0 ? (
										filteredRoles.map(role => (
											<div key={role.slug} className="role-card">
												<div className="role-card-header">
													<div className="role-card-info">
														<h3 className="role-card-name">{role.label}</h3>
														<p className="role-card-description">{role.description || "Pas de description"}</p>
													</div>
													<div className="role-card-status">
														<span className="role-badge role-badge-active">
															Priorité: {role.priority}
														</span>
													</div>
												</div>
												<div className="role-card-actions">
													<button
														className="role-btn role-btn-secondary"
														onClick={() => handleEditRole(role)}
														disabled={saving === role.slug}
													>
														Modifier
													</button>
													<button
														className="role-btn role-btn-danger"
														onClick={() => handleDeleteRole(role)}
														disabled={saving === role.slug}
													>
														Supprimer
													</button>
												</div>
											</div>
										))
									) : (
										<div className="role-empty">
											<p>Aucun rôle trouvé</p>
											<button className="role-btn role-btn-primary" onClick={handleNewRole}>
												Créer le premier rôle
											</button>
										</div>
									)}
								</div>
							</>
						) : (
							<div className="role-form-container">
								<div className="role-form-header">
									<h2>{editingRole ? "Modifier le rôle" : "Créer un nouveau rôle"}</h2>
								</div>

								<form onSubmit={handleSaveRole} className="role-form">
									<div className="role-form-group">
										<label htmlFor="slug" className="role-form-label">
											Identifiant (slug) <span className="role-required">*</span>
										</label>
										<input
											type="text"
											id="slug"
											name="slug"
											className="role-form-input"
											value={formData.slug}
											onChange={handleFormChange}
											placeholder="Ex: admin, moderator, user..."
											disabled={!!editingRole}
											required
										/>
									</div>

									<div className="role-form-group">
										<label htmlFor="label" className="role-form-label">
											Nom (label) <span className="role-required">*</span>
										</label>
										<input
											type="text"
											id="label"
											name="label"
											className="role-form-input"
											value={formData.label}
											onChange={handleFormChange}
											placeholder="Ex: Administrateur, Modérateur..."
											required
										/>
									</div>

									<div className="role-form-group">
										<label htmlFor="description" className="role-form-label">
											Description
										</label>
										<textarea
											id="description"
											name="description"
											className="role-form-textarea"
											value={formData.description}
											onChange={handleFormChange}
											placeholder="Description du rôle et de ses responsabilités..."
											rows="4"
										/>
									</div>

									<div className="role-form-group">
										<label htmlFor="priority" className="role-form-label">
											Priorité
										</label>
										<input
											type="number"
											id="priority"
											name="priority"
											className="role-form-input"
											value={formData.priority || ""}
											onChange={handleFormChange}
											placeholder="Ordre de priorité du rôle..."
										/>
									</div>

									<div className="role-form-actions">
										<button
											type="button"
											className="role-btn role-btn-secondary"
											onClick={handleCancel}
											disabled={saving}
										>
											Annuler
										</button>
										<button
											type="submit"
											className="role-btn role-btn-primary"
											disabled={saving}
										>
											{saving ? "Enregistrement..." : editingRole ? "Mettre à jour" : "Créer"}
										</button>
									</div>
								</form>
							</div>
						)}
					</div>
				) : (
					<div className="role-section role-permission-section">
						<div className="role-permission-placeholder">
							<h2>Gestion des Permissions</h2>
							<p>Cette section permettra de gérer les permissions associées aux rôles.</p>
							<p>À développer...</p>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
